import { db, auth } from '../../../../../firebaseConfig';
import {
  doc,
  getDoc,
  updateDoc,
  collection,
  addDoc,
  serverTimestamp,
} from 'firebase/firestore';

// Sync project_members collection with team members from draft
const syncProjectMembers = async (projectId, teamMembers, creatorId) => {
  try {
    const membersRef = collection(db, 'project_members');

    // Get existing members for this project
    const { getDocs, query, where, deleteDoc } = await import('firebase/firestore');
    const existingQuery = query(membersRef, where('projectId', '==', projectId));
    const existingSnap = await getDocs(existingQuery);
    const existingMemberIds = new Set();
    const existingDocs = {};

    existingSnap.forEach(docSnap => {
      const data = docSnap.data();
      existingMemberIds.add(data.userId);
      existingDocs[data.userId] = { id: docSnap.id, ...data };
    });

    // Determine adds and removes
    const newMemberIds = new Set(teamMembers.map(m => m.id).filter(Boolean));

    // Add new members (including creator if not present)
    for (const member of teamMembers) {
      if (!member.id) continue;

      if (!existingMemberIds.has(member.id)) {
        // Add new member
        await addDoc(membersRef, {
          projectId,
          userId: member.id,
          role: member.role || (member.id === creatorId ? 'Creator' : 'Member'),
          joinedAt: serverTimestamp(),
        });
      } else if (existingDocs[member.id]?.role !== member.role) {
        // Update role if changed
        const memberDocRef = doc(db, 'project_members', existingDocs[member.id].id);
        await updateDoc(memberDocRef, { role: member.role || 'Member' });
      }
    }

    // Remove members no longer in team (but never remove creator)
    for (const userId of existingMemberIds) {
      if (!newMemberIds.has(userId) && userId !== creatorId) {
        const memberDocRef = doc(db, 'project_members', existingDocs[userId].id);
        await deleteDoc(memberDocRef);
      }
    }
  } catch (error) {
    console.warn('Failed to sync project members:', error);
    // Non-fatal - draft is saved, membership sync is secondary
  }
};

// Create a project document in Firestore
export const saveProjectToFirebase = async (project) => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('User must be authenticated to create a project');
    }

    const projectsRef = collection(db, 'projects');

    const teamMembers = Array.isArray(project.teamMembers) ? project.teamMembers : [];
    const teamMemberIds = teamMembers.map(m => m.id).filter(Boolean);

    const sections = Array.isArray(project.sections)
      ? project.sections.map(s => ({ id: s.id || null, title: s.title || '', body: s.body || '' }))
      : [];

    const phases = Array.isArray(project.phases)
      ? project.phases.map(p => ({
        id: p.id || null,
        objective: p.objective || '',
        tasks: Array.isArray(p.tasks)
          ? p.tasks.map(t => ({ id: t.id || null, title: t.title || '', content: t.content || '' }))
          : [],
      }))
      : [];

    const projectData = {
      creatorId: currentUser.uid,
      title: project.title || project.name || '',
      issue: project.issue || '',
      scale: project.scale || '',
      location: project.location || '',
      imageUrl: project.imageUrl || '',
      mission: project.mission || '',
      solutionType: project.solutionType || '',
      sourceSolutionId: project.sourceSolutionId || null,
      sections,
      phases,
      teamMemberIds,
      teamMembers,
      status: project.status || 'active',
      likeCount: 0,
      followerCount: 0,
      shareCount: 0,
      updatesCount: 0,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    const docRef = await addDoc(projectsRef, projectData);
    const createdSnap = await getDoc(docRef);
    const createdData = createdSnap.data() || {};

    return {
      id: docRef.id,
      ...createdData,
      createdAt: createdData.createdAt?.toDate?.() || new Date(),
      updatedAt: createdData.updatedAt?.toDate?.() || new Date(),
    };
  } catch (error) {
    console.error('Error saving project:', error);
    throw error;
  }
};

// Save a project draft to Firestore (allows incomplete data)
export const saveDraftToFirebase = async (draftData, existingDraftId = null) => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('User must be authenticated to save a draft');
    }

    const projectsRef = collection(db, 'projects');

    // Normalize team members
    const teamMembers = Array.isArray(draftData.teamMembers) ? draftData.teamMembers : [];
    const teamMemberIds = teamMembers.map(m => m.id).filter(Boolean);

    // Normalize sections
    const sections = Array.isArray(draftData.sections)
      ? draftData.sections.map(s => ({
        id: s.id || null,
        title: s.title || '',
        body: s.body || ''
      }))
      : [];

    // Normalize phases with task ownership and assignment tracking
    const phases = Array.isArray(draftData.phases)
      ? draftData.phases.map(p => ({
        id: p.id || null,
        objective: p.objective || '',
        tasks: Array.isArray(p.tasks)
          ? p.tasks.map(t => ({
            id: t.id || null,
            title: t.title || '',
            content: t.content || '',
            createdBy: t.createdBy || currentUser.uid, // Track task creator
            assignedTo: Array.isArray(t.assignedTo) ? t.assignedTo : [], // Assigned members
            createdAt: t.createdAt || new Date()
          }))
          : [],
      }))
      : [];

    const projectData = {
      creatorId: currentUser.uid,
      title: draftData.title || draftData.projectTitle || '',
      issue: draftData.issue || '',
      scale: draftData.scale || '',
      location: draftData.location || '',
      imageUrl: draftData.imageUrl || '',
      mission: draftData.mission || '',
      solutionType: draftData.solutionType || '',
      sourceSolutionId: draftData.sourceSolutionId || null,
      sections,
      phases,
      teamMemberIds,
      teamMembers,
      status: 'draft', // KEY: Mark as draft
      likeCount: 0,
      followerCount: 0,
      shareCount: 0,
      updatesCount: 0,
      updatedAt: serverTimestamp(),
    };

    let docId;
    let savedData;

    if (existingDraftId) {
      // Update existing draft
      const draftRef = doc(db, 'projects', existingDraftId);
      await updateDoc(draftRef, projectData);
      const updatedSnap = await getDoc(draftRef);
      savedData = updatedSnap.data() || {};
      docId = existingDraftId;
    } else {
      // Create new draft
      projectData.createdAt = serverTimestamp();
      const docRef = await addDoc(projectsRef, projectData);
      const createdSnap = await getDoc(docRef);
      savedData = createdSnap.data() || {};
      docId = docRef.id;
    }

    // Sync project_members collection for team collaboration
    // This allows team members to see the draft in YourProjectsPage
    await syncProjectMembers(docId, teamMembers, currentUser.uid);

    return {
      id: docId,
      ...savedData,
      createdAt: savedData.createdAt?.toDate?.() || new Date(),
      updatedAt: savedData.updatedAt?.toDate?.() || new Date(),
    };
  } catch (error) {
    console.error('Error saving draft:', error);
    throw error;
  }
};
